import { useCauserie } from '@contexts/CauserieContext/CauserieContext';
import { Question, useGetQuestionsMutation, useGetTalksMutation } from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type Answerable<T> = T & {
    answer: number | null;
    answerToDisplay?: number;
};

export const useCauseriesTalkQuestionsPageVM = () => {
    const { thematicId, talkId } = useParams();
    const [questions, setQuestions] = useState<Answerable<Question>[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [pageTitle, setPageTitle] = useState<string>('');
    const [showResults, setShowResults] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [currentScore, setCurrentScore] = useState<number>(0);
    const [explanationsToDisplay, setExplanationsToDisplay] = useState<number[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

    const { setQuestionsAnswers, setScore } = useCauserie();

    const navigate = useNavigate();

    const [getTalks, { isLoading: isLoadingTalks, data: talks }] = useGetTalksMutation();
    const [getQuestions, { isLoading: isLoadingQuestions, data, error }] = useGetQuestionsMutation();

    const canGoBack = useMemo(() => {
        return currentQuestionIndex > 0 && !showResults;
    }, [currentQuestionIndex, showResults]);

    const canGoNext = useMemo(() => {
        if (showResults) return false;
        if (!questions.length) return false;

        const currentQuestion = questions[currentQuestionIndex];
        return currentQuestion && currentQuestion.answer !== null;
    }, [currentQuestionIndex, questions, showResults]);

    const isLastQuestion = useMemo(() => {
        return currentQuestionIndex === questions.length - 1;
    }, [currentQuestionIndex, questions.length]);

    const handleNext = () => {
        if (isLastQuestion) {
            setShowResults(true);
            return;
        }
        setCurrentQuestionIndex(currentQuestionIndex + 1);
    };

    const handlePrev = () => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
    };

    const selectAnswer = (questionId: number, answerId: number) => {
        const question = questions.find((question) => question.id === questionId);
        if (!question) {
            return;
        }

        const hasAlreadyAnswered = question.answer !== null;
        if (hasAlreadyAnswered) {
            return;
        }

        setExplanationsToDisplay((prev) => [...prev, questionId]);

        const rightAnswer = question.talk_answers.find((answer) => answer.is_correct);

        setQuestions(
            questions.map((question) => {
                if (question.id === questionId) {
                    return { ...question, answer: answerId, answerToDisplay: rightAnswer?.id };
                }
                return question;
            })
        );

        setTimeout(() => {
            const scrollContainer = document.querySelector('.rendered-page') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
            }
        }, 100);
    };

    const isEveryQuestionAnswered = useMemo(() => {
        if (isLoadingTalks) {
            return false;
        }
        if (!questions.length) {
            return false;
        }

        return questions.every((question) => question.answer !== null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questions]);

    useEffect(() => {
        if (!talkId || !thematicId) return;
        getQuestions({ talkId: parseInt(talkId) });
        getTalks({ thematicId: parseInt(thematicId) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [talkId, thematicId]);

    useEffect(() => {
        if (data) {
            setQuestions(data.map((question) => ({ ...question, answer: null })));
        }

        if (error) {
            setErrorMessage('Une erreur est survenue lors du chargement des questions');
        }
    }, [isLoadingQuestions, data, error]);

    useEffect(() => {
        if (!talks || !talkId) return;

        const currentTalk = talks.find((talk) => talk.id === parseInt(talkId));

        if (!currentTalk) {
            setErrorMessage('Causerie non trouvée');
            return;
        }

        setPageTitle(currentTalk.title);
    }, [talks, talkId]);

    useEffect(() => {
        if (!showResults || !isEveryQuestionAnswered || !talkId) {
            return;
        }

        const scoreValue = questions.filter((question) => question.answer === question.answerToDisplay).length;
        setCurrentScore(scoreValue);

        if (!validated) {
            return;
        }

        setQuestionsAnswers(
            questions.map((question) => ({
                question_id: question.id,
                answer_id: question.answer ?? 0
            }))
        );
        setScore(scoreValue);
    }, [showResults, questions, talkId, setQuestionsAnswers, setScore, isEveryQuestionAnswered, validated]);

    return {
        thematicId,
        talkId,
        pageTitle,
        questions,
        isLoading: isLoadingTalks || isLoadingQuestions,
        errorMessage,
        selectAnswer,
        explanationsToDisplay,
        isEveryQuestionAnswered,
        navigate,
        showResults,
        setShowResults,
        currentScore,
        validated,
        setValidated,
        currentQuestionIndex,
        canGoBack,
        canGoNext,
        isLastQuestion,
        handleNext,
        handlePrev
    };
};
